import { z } from 'zod';

/**
 * API Error Response structure
 */
export interface ApiErrorResponse {
	type: string;
	fullError: string;
	instance: string;
	stackTrace: string;
	status: number;
	title: string;
	/** Request URL for debugging (not displayed in UI) */
	requestUrl?: string;
	/** Request payload for debugging (not displayed in UI) */
	requestPayload?: string;
}

export interface RequestInfo {
	url?: string;
	payload?: unknown;
}

const HTTP_STATUS_TITLES = new Map([
	[400, 'Bad Request'],
	[401, 'Unauthorized'],
	[403, 'Forbidden'],
	[404, 'Not Found'],
	[405, 'Method Not Allowed'],
	[408, 'Request Timeout'],
	[409, 'Conflict'],
	[410, 'Gone'],
	[422, 'Unprocessable Entity'],
	[429, 'Too Many Requests'],
	[500, 'Internal Server Error'],
	[501, 'Not Implemented'],
	[502, 'Bad Gateway'],
	[503, 'Service Unavailable'],
	[504, 'Gateway Timeout'],
]);

/** Problem-details body returned by the API; every field is optional because the backend is not consistent. */
const problemDetailsSchema = z.object({
	type: z.string().optional(),
	fullError: z.string().optional(),
	instance: z.string().optional(),
	stackTrace: z.string().optional(),
	status: z.number().optional(),
	title: z.string().optional(),
});

const apiErrorResponseSchema = z.object({
	status: z.number(),
	title: z.string(),
});

const axiosErrorSchema = z.object({
	response: z.object({
		status: z.number().optional(),
		data: z.unknown(),
	}),
	config: z
		.object({
			url: z.string().optional(),
			data: z.string().optional(),
		})
		.optional(),
});

export function getStatusTitle(status: number): string {
	return HTTP_STATUS_TITLES.get(status) ?? `Error ${status}`;
}

export function isClientError(status: number): boolean {
	return status >= 400 && status < 500;
}

export function isServerError(status: number): boolean {
	return status >= 500 && status < 600;
}

/**
 * Type guard to check if an unknown value is an ApiErrorResponse
 */
export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
	return apiErrorResponseSchema.safeParse(value).success;
}

/**
 * HTTP status of a failed request, for both hey-api errors (problem-details body) and axios errors.
 */
export function getErrorStatus(error: unknown): number | null {
	const apiError = apiErrorResponseSchema.safeParse(error);

	if (apiError.success) {
		return apiError.data.status;
	}

	const axiosError = axiosErrorSchema.safeParse(error);

	return axiosError.data?.response.status ?? null;
}

function formatPayload(payload: string): string {
	try {
		return JSON.stringify(JSON.parse(payload), null, 2);
	} catch {
		return payload;
	}
}

/**
 * Parse an axios error response into ApiErrorResponse
 */
export function parseApiError(error: unknown): ApiErrorResponse | null {
	const axiosError = axiosErrorSchema.safeParse(error);

	if (!axiosError.success) {
		return null;
	}

	const { response, config } = axiosError.data;
	const data = problemDetailsSchema.catch({}).parse(response.data);

	return {
		type: data.type || 'Unknown Error',
		fullError: data.fullError || data.title || 'An unknown error occurred',
		instance: data.instance ?? '',
		stackTrace: data.stackTrace ?? '',
		status: data.status || response.status || 500,
		title: data.title || 'An error occurred',
		requestUrl: config?.url,
		requestPayload: config?.data ? formatPayload(config.data) : undefined,
	};
}

/**
 * Build ApiErrorResponse from an error thrown before any response was received
 * (auth token failure, request serialization, network failure).
 */
export function buildApiErrorFromClientError(error: unknown, request?: RequestInfo): ApiErrorResponse {
	const isError = error instanceof Error;
	const message = isError ? error.message : String(error);

	return {
		status: 0,
		title: isError && error.name === 'TypeError' && message.includes('fetch') ? 'Network Error' : 'Client Error',
		type: isError ? error.name : 'Unknown Error',
		fullError: message,
		stackTrace: isError ? (error.stack ?? '') : '',
		instance: '',
		requestUrl: request?.url,
		requestPayload: request?.payload ? JSON.stringify(request.payload, null, 2) : undefined,
	};
}

/**
 * Build ApiErrorResponse from @hey-api/client-fetch error and response
 */
export function buildApiErrorFromResponse(
	error: unknown,
	response: { status: number; statusText: string },
	request?: RequestInfo,
): ApiErrorResponse {
	const data = problemDetailsSchema.catch({}).parse(error);

	return {
		status: response.status,
		title: data.title || response.statusText,
		type: data.type || 'Unknown Error',
		fullError: data.fullError ?? '',
		stackTrace: data.stackTrace ?? '',
		instance: data.instance ?? '',
		requestUrl: request?.url,
		requestPayload: request?.payload ? JSON.stringify(request.payload, null, 2) : undefined,
	};
}
